import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { Box, Button, Menu, MenuItem } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { blue, red, green, orange } from '@mui/material/colors';
import { Position } from '../types/position.ts';

function downsample(data: number[][], maxPoints: number) {
  if (!data || data.length === 0) return [];
  const sampled = [];
  const step = Math.max(1, Math.ceil(data.length / maxPoints));
  for (let i = 0; i < data.length; i += step) {
    sampled.push(data[i]);
  }
  return sampled;
}

function normalizeChartPositions(data: Position[] | Position[][]): Position[] {
  const groups = Array.isArray(data[0]) ? (data as Position[][]) : [data as Position[]];
  const normalized: Position[] = [];
  let distanceOffset = 0;
  let durationOffset = 0;

  for (const group of groups) {
    if (!group.length) continue;
    const adjustedGroup = group.map((pos) => {
      const item = pos as any;
      return {
        ...item,
        accumDistance: typeof item.accumDistance === 'number' ? item.accumDistance + distanceOffset : item.accumDistance,
        accumDuration: typeof item.accumDuration === 'number' ? item.accumDuration + durationOffset : item.accumDuration,
      };
    });

    const last = adjustedGroup[adjustedGroup.length - 1];
    if (typeof last.accumDistance === 'number') {
      distanceOffset = last.accumDistance;
    }
    if (typeof last.accumDuration === 'number') {
      durationOffset = last.accumDuration;
    }

    normalized.push(...adjustedGroup);
  }

  return normalized;
}

function mergeAggregatedRun(run: Position[]): Position {
  const first = run[0];
  const merged: any = { ...first };
  const metrics = ['altitude', 'speed', 'bearing', 'accuracy', 'velocity', 'acceleration'];

  metrics.forEach((metric) => {
    const values = run
      .map((pos) => (pos as any)[metric])
      .filter((value): value is number => typeof value === 'number');
    if (values.length > 0) {
      merged[metric] = values.reduce((sum, value) => sum + value, 0) / values.length;
    } else {
      merged[metric] = (run[run.length - 1] as any)[metric] ?? null;
    }
  });

  const firstItem = first as any;
  merged.accumDistance = firstItem.accumDistance;
  merged.accumDuration = firstItem.accumDuration;
  merged.time = firstItem.time;
  return merged;
}

interface ChartAggregation {
  attribute: string;
  values: Array<string | number>;
}

function compressAggregatedChartPositions(positions: Position[], aggregation: ChartAggregation): Position[] {
  const compressed: Position[] = [];
  let run: Position[] = [];
  const keyOf = (pos: Position) => (pos as any)[aggregation.attribute];

  const flushRun = () => {
    if (run.length > 0) {
      compressed.push(mergeAggregatedRun(run));
      run = [];
    }
  };

  for (const pos of positions) {
    if (aggregation.values.includes(keyOf(pos))) {
      if (run.length > 0 && keyOf(run[0]) !== keyOf(pos)) flushRun();
      run.push(pos);
    } else {
      flushRun();
      compressed.push(pos);
    }
  }

  flushRun();
  return compressed;
}

function isCategoricalMetric(metric: YAxisMetric): metric is 'provider' | 'travel_mode' {
  return metric === 'provider' || metric === 'travel_mode';
}

function getMetricCategories(metric: YAxisMetric, positions: Position[]): string[] {
  if (!isCategoricalMetric(metric)) return [];
  const categories: string[] = [];
  for (const pos of positions) {
    const value = (pos as any)[metric];
    if (typeof value === 'string' && !categories.includes(value)) {
      categories.push(value);
    }
  }
  return categories;
}

function formatMetricLabel(metric: string) {
  return metric
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getMetricCategoryLabels(metric: YAxisMetric, positions: Position[]): string[] {
  if (!isCategoricalMetric(metric)) return [];
  const values = new Set<string>();
  for (const pos of positions) {
    const value = (pos as any)[metric];
    if (typeof value === 'string' && !values.has(value)) {
      values.add(value);
    }
  }
  return Array.from(values).map(formatMetricLabel);
}

function metricValueAsNumber(metric: YAxisMetric, pos: Position, categories: string[]): number | null {
  const value = (pos as any)[metric];
  if (isCategoricalMetric(metric)) {
    return typeof value === 'string' ? categories.indexOf(formatMetricLabel(value)) : null;
  }
  return typeof value === 'number' ? value : null;
}

type XAxisType = 'distance' | 'time' | 'timestamp';
type YAxisMetric = 'altitude' | 'speed' | 'accuracy' | 'bearing' | 'provider' | 'travel_mode';

const METRIC_COLORS: Record<YAxisMetric, string> = {
  altitude: red[800],
  speed: blue[800],
  accuracy: green[800],
  bearing: orange[800],
  provider: '#7e57c2',
  travel_mode: '#26a69a'
};

const METRIC_UNITS: Record<YAxisMetric, string> = {
  altitude: 'm',
  speed: 'm/s',
  accuracy: 'm',
  bearing: '°',
  provider: '',
  travel_mode: ''
};

interface PathChartsProps {
  data: Position[] | Position[][];
  aggregation: ChartAggregation;
  width?: string;
  height?: string;
}

const PathCharts: React.FC<PathChartsProps> = ({
  data,
  aggregation,
  width = "100%",
  height = "100%"
}) => {
  const positions = useMemo(() => {
    const normalized = normalizeChartPositions(data);
    return compressAggregatedChartPositions(normalized, aggregation);
  }, [data, aggregation]);
  const [xAxisType, setXAxisType] = useState<XAxisType>('distance');
  const [selectedMetrics, setSelectedMetrics] = useState<Array<YAxisMetric | null>>(['altitude', 'speed']);

  // Menu state for axis selection
  const [xAxisMenuAnchor, setXAxisMenuAnchor] = useState<HTMLElement | null>(null);
  const [yAxisMenuAnchor, setYAxisMenuAnchor] = useState<HTMLElement | null>(null);
  const [yAxisMenuIndex, setYAxisMenuIndex] = useState<0 | 1>(0);

  // Axis title positions and elements
  const [axisTitlePositions, setAxisTitlePositions] = useState({
    xAxis: { x: 0, y: 0, element: null as HTMLElement | null },
    yAxis: [
      { x: 0, y: 0, element: null as HTMLElement | null },
      { x: 0, y: 0, element: null as HTMLElement | null }
    ]
  });
  const [axisPositionsReady, setAxisPositionsReady] = useState(false);
  const [chartHeight, setChartHeight] = useState<number | undefined>(undefined);
  const chartRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const xAxisOverlayRef = useRef<HTMLButtonElement | null>(null);
  const yAxisOverlayRefs = useRef<(HTMLButtonElement | null)[]>([null, null]);

  // Create series data - memoized to avoid recreating on every render
  const seriesDataMap = useMemo(() => {
    const map: Record<YAxisMetric, number[][]> = {
      altitude: [],
      speed: [],
      accuracy: [],
      bearing: [],
      provider: [],
      travel_mode: []
    };

    if (!positions || positions.length === 0) return map;

    (Object.keys(map) as YAxisMetric[]).forEach((metric: YAxisMetric) => {
      const metricData: number[][] = [];
      const categories = isCategoricalMetric(metric) ? getMetricCategoryLabels(metric, positions) : [];
      positions.forEach((p: any) => {
        const xValue = xAxisType === 'distance'
          ? (p as any).accumDistance
          : xAxisType === 'time'
            ? (p as any).accumDuration
            : new Date((p as any).time).getTime();
        const yValue = metricValueAsNumber(metric, p, categories);
        const hasXValue = typeof xValue === 'number' && xValue !== null;
        const hasYValue = typeof yValue === 'number' && yValue !== null;

        if (hasXValue && hasYValue) {
          metricData.push([xValue, yValue as number]);
        }
      });
      map[metric] = downsample(metricData, 500);
    });

    return map;
  }, [data, xAxisType]);

  // Build y-axes configuration - always 2 axes, second can be empty
  const yAxes: Highcharts.YAxisOptions[] = useMemo(() => {
    const metric0 = selectedMetrics[0];
    const metric1 = selectedMetrics[1];
    const categories0 = metric0 && isCategoricalMetric(metric0) ? getMetricCategoryLabels(metric0, positions) : undefined;
    const categories1 = metric1 && isCategoricalMetric(metric1) ? getMetricCategoryLabels(metric1, positions) : undefined;
    const axes: Highcharts.YAxisOptions[] = [];

    axes[0] = {
      title: {
        text: metric0
          ? `${formatMetricLabel(metric0)}${categories0 ? '' : ` (${METRIC_UNITS[metric0]})`}`
          : 'Select Metric',
        style: metric0 ? {} : { color: 'transparent' },
        margin: 12
      },
      opposite: false,
      visible: true,
      showEmpty: true,
      gridLineWidth: categories0 ? 0 : 1,
      labels: {
        enabled: Boolean(metric0),
        formatter: function (this: any) {
          if (categories0) {
            return categories0[this.value as number] ?? this.value;
          }
          return this.value;
        }
      },
      categories: categories0
    };
    axes[1] = {
      title: {
        text: metric1
          ? `${formatMetricLabel(metric1)}${categories1 ? '' : ` (${METRIC_UNITS[metric1]})`}`
          : 'Select Metric',
        style: metric1 ? {} : { color: 'transparent' }
      },
      opposite: true,
      visible: true,
      showEmpty: true,
      gridLineWidth: categories1 ? 0 : 1,
      labels: {
        enabled: Boolean(metric1),
        formatter: function (this: any) {
          if (categories1) {
            return categories1[this.value as number] ?? this.value;
          }
          return this.value;
        }
      },
      categories: categories1
    };
    return axes;
  }, [selectedMetrics, positions]);

  // Build series configuration
  const series = useMemo(() =>
    selectedMetrics.map((metric: YAxisMetric | null, idx: number) => {
      const categories = metric && isCategoricalMetric(metric) ? getMetricCategoryLabels(metric, positions) : [];
      return {
        id: `series-${idx}`,
        name: metric ? formatMetricLabel(metric) : `Axis ${idx + 1}`,
        data: metric ? seriesDataMap[metric] : [],
        type: 'line' as const,
        color: metric ? METRIC_COLORS[metric] : 'transparent',
        yAxis: idx,
        showInLegend: Boolean(metric),
        enableMouseTracking: Boolean(metric),
        visible: Boolean(metric),
        step: metric && isCategoricalMetric(metric) ? 'left' as const : undefined,
        custom: { metric, categories }
      };
    }),
    [selectedMetrics, seriesDataMap, positions]
  );

  const xAxisTitle = xAxisType === 'distance'
    ? 'Distance (km)'
    : xAxisType === 'time'
      ? 'Time (h)'
      : 'Timestamp';

  const getDropdownColor = (idx: number, metric: YAxisMetric | null) => {
    if (idx === 0) {
      return metric ? METRIC_COLORS[metric] : 'text.secondary';
    }
    return metric ? METRIC_COLORS[metric] : 'text.secondary';
  };

  const chartContainerProps = useMemo(
    () => ({ style: { width: '100%', height: '100%', overflow: 'hidden' } }),
    []
  );

  const onChartLoad = useCallback(function (this: any) {
    chartRef.current = this;
  }, []);

  const onChartRedraw = useCallback(function (this: any) {
    chartRef.current = this;
  }, []);

  // Check if we have any valid data for the selected metrics
  const hasValidData = useMemo(() => {
    return selectedMetrics.some((metric) => metric !== null && seriesDataMap[metric].length > 0);
  }, [selectedMetrics, seriesDataMap]);

  const handleOpenXAxisMenu = (e: React.MouseEvent<HTMLElement>) => {
    setXAxisMenuAnchor(xAxisOverlayRef.current);
  };

  const handleOpenYAxisMenu = (index: 0 | 1) => (e: React.MouseEvent<HTMLElement>) => {
    setYAxisMenuIndex(index);
    setYAxisMenuAnchor(yAxisOverlayRefs.current[index]);
  };

  const handleXAxisSelect = (type: XAxisType) => {
    setXAxisType(type);
    setXAxisMenuAnchor(null);
  };

  const handleYAxisSelect = (metric: YAxisMetric | null) => {
    setSelectedMetrics((current) => {
      const next = [...current];
      next[yAxisMenuIndex] = metric;
      return next;
    });
    setYAxisMenuAnchor(null);
  };

  // Update axis title positions when chart changes
  useEffect(() => {
    let resizeTimer: NodeJS.Timeout | null = null;
    let rafId: number | null = null;

    const measurePositions = (chart: any, container: HTMLElement) => {
      const containerRect = container.getBoundingClientRect();
      const newPositions = {
        xAxis: { x: 0, y: 0, element: null as HTMLElement | null },
        yAxis: [
          { x: 0, y: 0, element: null as HTMLElement | null },
          { x: 0, y: 0, element: null as HTMLElement | null }
        ]
      };

      if (chart.xAxis?.[0]?.axisTitle) {
        const titleElement = chart.xAxis[0].axisTitle.element;
        const titleRect = titleElement.getBoundingClientRect();
        newPositions.xAxis = {
          x: titleRect.left - containerRect.left + titleRect.width / 2,
          y: titleRect.top - containerRect.top + titleRect.height / 2,
          element: titleElement
        };
      }

      (chart.yAxis || []).forEach((axis: any, idx: number) => {
        if (idx < 2) {
          if (axis.axisTitle && axis.axisTitle.element) {
            const titleElement = axis.axisTitle.element;
            const titleRect = titleElement.getBoundingClientRect();
            newPositions.yAxis[idx] = {
              x: titleRect.left - containerRect.left + titleRect.width / 2,
              y: titleRect.top - containerRect.top + titleRect.height / 2,
              element: titleElement
            };
          } else {
            const fallbackX = typeof axis.left === 'number'
              ? axis.left + (axis.width || 0) / 2
              : (idx === 0 ? 30 : containerRect.width - 30);
            const fallbackY = typeof axis.top === 'number'
              ? axis.top + (axis.height || 0) / 2
              : containerRect.height / 2;
            newPositions.yAxis[idx] = {
              x: fallbackX,
              y: fallbackY,
              element: null
            };
          }
        }
      });

      return newPositions;
    };

    const updateAxisPositions = () => {
      if (!hasValidData || !chartRef.current || !containerRef.current) return;
      const chart = chartRef.current;
      const container = containerRef.current;

      const newPositions = measurePositions(chart, container);
      setAxisTitlePositions(newPositions);
      setAxisPositionsReady(true);
      setChartHeight(container.clientHeight || undefined);

      if (chart && typeof chart.reflow === 'function') {
        chart.reflow();
      }

      if (rafId) {
        window.cancelAnimationFrame(rafId);
      }
      rafId = window.requestAnimationFrame(() => {
        if (!chartRef.current || !containerRef.current) return;
        const finalPositions = measurePositions(chartRef.current, containerRef.current);
        setAxisTitlePositions(finalPositions);
      });
    };

    const timer = setTimeout(updateAxisPositions, 100);

    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateAxisPositions, 50);
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(updateAxisPositions, 50);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timer);
      if (resizeTimer) clearTimeout(resizeTimer);
      if (rafId) window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedMetrics, xAxisType, hasValidData]);

  // Effect to trigger chart reflow when dimensions or metrics change
  useEffect(() => {
    if (!hasValidData) {
      chartRef.current = null;
      return;
    }

    const triggerReflow = () => {
      if (chartRef.current && typeof chartRef.current.reflow === 'function') {
        setTimeout(() => {
          if (chartRef.current && typeof chartRef.current.reflow === 'function') {
            chartRef.current.reflow();
          }
        }, 0);
      }
    };

    triggerReflow();
  }, [selectedMetrics, xAxisType, hasValidData]);

  const handleZoom = useCallback((event: any) => {
    const chart = event.target || chartRef.current;
    if (!chart) return false;

    if (!event.xAxis) {
      // Reset zoom
      selectedMetrics.forEach((metric, idx) => {
        if (metric && chart.series && chart.series[idx]) {
          chart.series[idx].setData(seriesDataMap[metric], false);
        }
      });
      if (chart.resetZoomButton) {
        chart.resetZoomButton.destroy();
        chart.resetZoomButton = null;
      }
      chart.redraw();
      return false;
    } else {
      const min = event.xAxis[0].min;
      const max = event.xAxis[0].max;
      selectedMetrics.forEach((metric, idx) => {
        if (metric && chart.series && chart.series[idx]) {
          const categories = isCategoricalMetric(metric) ? getMetricCategoryLabels(metric, positions) : [];
          const fullData = positions
            .filter((p: any) => {
              const xValue = xAxisType === 'distance'
                ? (p as any).accumDistance
                : xAxisType === 'time'
                  ? (p as any).accumDuration
                  : new Date((p as any).time).getTime();
              const yValue = metricValueAsNumber(metric, p, categories);
              const hasXValue = typeof xValue === 'number' && xValue !== null;
              const hasYValue = typeof yValue === 'number' && yValue !== null;
              return hasXValue && hasYValue && xValue >= min && xValue <= max;
            })
            .map((p: any) => {
              const xValue = xAxisType === 'distance'
                ? (p as any).accumDistance
                : xAxisType === 'time'
                  ? (p as any).accumDuration
                  : new Date((p as any).time).getTime();
              const yValue = metricValueAsNumber(metric, p, categories);
              return [xValue, yValue];
            });
          const sampledData = downsample(fullData, 500);
          chart.series[idx].setData(sampledData, false);
        }
      });
      chart.redraw();
      chart.showResetZoom();
      return false;
    }
  }, [selectedMetrics, seriesDataMap, xAxisType, data]);

  const options: Highcharts.Options = useMemo(() => ({
    chart: {
      type: 'line',
      zoomType: 'x',
      backgroundColor: 'transparent',
      spacingTop: 8,
      spacingRight: 3,
      spacingBottom: 3,
      spacingLeft: 3,
      ...(chartHeight ? { height: chartHeight } : {}),
      events: {
        selection: handleZoom,
        load: onChartLoad,
        redraw: onChartRedraw
      }
    } as any,
    title: { text: '' },
    xAxis: {
      title: { text: xAxisTitle, margin: 12 },
      type: xAxisType === 'timestamp' ? 'datetime' : 'linear'
    },
    yAxis: yAxes,
    tooltip: {
      shared: true,
      formatter: function () {
        // @ts-ignore
        const points = this.points;
        const x = points?.[0]?.x;
        const xLabel = xAxisType === 'distance'
          ? `Distance: ${x?.toFixed(2)} km`
          : xAxisType === 'time'
            ? `Time: ${x?.toFixed(2)} h`
            : `Timestamp: ${x != null ? new Date(x).toLocaleString() : ''}`;
        let html = `<b>${xLabel}</b><br/>`;
        points?.forEach((p: any) => {
          const metric = p.series?.options?.custom?.metric as YAxisMetric | null;
          const categories = p.series?.options?.custom?.categories as string[] | undefined;
          const valueLabel = metric && isCategoricalMetric(metric) && categories
            ? categories[p.y as number] ?? p.y
            : (typeof p.y === 'number' ? p.y.toFixed(2) : p.y);
          html += `<span style="color:${p.color}">●</span> ${p.series.name}: <b>${valueLabel}</b><br/>`;
        });
        return html;
      }
    },
    series,
    credits: { enabled: false },
    legend: {
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'top',
      floating: true,
      y: 4,
      backgroundColor: 'rgba(255,255,255,0.7)',
      borderWidth: 0,
      itemStyle: {
        fontWeight: '500',
        fontSize: '12px'
      }
    },
    plotOptions: {
      line: {
        lineWidth: 2,
        marker: {
          enabled: false
        },
        states: {
          hover: {
            lineWidth: 3
          }
        }
      },
      series: {
        animation: false
      }
    }
  }), [chartHeight, xAxisTitle, yAxes, series, handleZoom, onChartLoad, onChartRedraw]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: height, width: width, overflow: 'hidden', position: 'relative', minHeight: 0 }} ref={containerRef}>
      {/* Chart Container */}
      {hasValidData ? (
        <Box sx={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 0, width: '100%', height: '100%' }}>
          <HighchartsReact
            highcharts={Highcharts}
            immutable={true}
            options={options}
            containerProps={chartContainerProps}
          />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%', color: 'text.secondary', flexDirection: 'column', gap: 2 }}>
          <div>No data available</div>
          <Box sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            Go to filter settings to change filters or click on axis titles to change selected metrics.
          </Box>
        </Box>
      )}

      {/* Clickable Axis Title Overlays - positioned relative to container */}
      {hasValidData && axisTitlePositions.xAxis.element && (
        <Button
          ref={xAxisOverlayRef}
          variant="outlined"
          size="small"
          disableElevation
          endIcon={<ArrowDropDownIcon />}
          onClick={handleOpenXAxisMenu}
          sx={{
            position: 'absolute',
            left: `${axisTitlePositions.xAxis.x}px`,
            top: `${axisTitlePositions.xAxis.y}px`,
            transform: 'translate(-50%, -50%)',
            cursor: 'pointer',
            borderRadius: '4px',
            backgroundColor: 'rgba(255,255,255,1)',
            border: 'none',
            color: 'text.secondary',
            fontSize: '11px',
            fontWeight: 'bold',
            zIndex: 100,
            whiteSpace: 'nowrap',
            pointerEvents: 'auto',
            textTransform: 'none',
            '& .MuiButton-endIcon svg': {
              color: 'currentColor'
            },
            '&:hover': {
              backgroundColor: 'rgba(245, 245, 245, 1)'
            }
          }}
        >
          {xAxisTitle}
        </Button>
      )}

      {/* Y-Axis Title Overlays - always render both axes */}
      {hasValidData && axisPositionsReady && [0, 1].map((idx) => {
        const metric = selectedMetrics[idx];
        const axisPos = axisTitlePositions.yAxis[idx];
        if (!axisPos) return null;
        return (
          <Button
            key={`y-axis-${idx}`}
            ref={(el) => { yAxisOverlayRefs.current[idx] = el as HTMLButtonElement | null; }}
            variant="outlined"
            size="small"
            disableElevation
            endIcon={<ArrowDropDownIcon />}
            onClick={handleOpenYAxisMenu(idx as 0 | 1)}
            sx={{
              position: 'absolute',
              left: `${axisPos.x}px`,
              top: `${axisPos.y}px`,
              transform: idx === 0 ? 'translate(-50%, -50%) rotate(-90deg)' : 'translate(-50%, -50%) rotate(90deg)',
              cursor: 'pointer',
              borderRadius: '4px',
              backgroundColor: 'rgba(255,255,255,1)',
              border: 'none',
              color: getDropdownColor(idx, metric),
              fontSize: '11px',
              fontWeight: 'bold',
              zIndex: 100,
              whiteSpace: 'nowrap',
              pointerEvents: 'auto',
              textTransform: 'none',
              '& .MuiButton-endIcon svg': {
                color: 'currentColor'
              },
              '&:hover': {
                backgroundColor: 'rgba(245, 245, 245, 1)'
              }
            }}
          >
            {metric ? `${formatMetricLabel(metric)}${METRIC_UNITS[metric] ? ` (${METRIC_UNITS[metric]})` : ''}` : 'Select Metric'}
          </Button>
        );
      })}

      {/* X-Axis Selector Menu */}
      <Menu
        anchorEl={xAxisMenuAnchor}
        open={Boolean(xAxisMenuAnchor)}
        onClose={() => setXAxisMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <MenuItem
          selected={xAxisType === 'distance'}
          onClick={() => handleXAxisSelect('distance')}
        >
          Distance (km)
        </MenuItem>
        <MenuItem
          selected={xAxisType === 'time'}
          onClick={() => handleXAxisSelect('time')}
        >
          Time (h)
        </MenuItem>
        <MenuItem
          selected={xAxisType === 'timestamp'}
          onClick={() => handleXAxisSelect('timestamp')}
        >
          Timestamp
        </MenuItem>
      </Menu>

      {/* Y-Axis Selector Menu */}
      <Menu
        anchorEl={yAxisMenuAnchor}
        open={Boolean(yAxisMenuAnchor)}
        onClose={() => setYAxisMenuAnchor(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        {yAxisMenuIndex === 1 && (
          <MenuItem onClick={() => handleYAxisSelect(null)}>
            None
          </MenuItem>
        )}
        {(['altitude', 'speed', 'accuracy', 'bearing', 'provider', 'travel_mode'] as YAxisMetric[]).map((metric: YAxisMetric) => {
          const otherAxisMetric = selectedMetrics[yAxisMenuIndex === 0 ? 1 : 0];
          return (
            <MenuItem
              key={metric}
              selected={selectedMetrics[yAxisMenuIndex] === metric}
              disabled={otherAxisMetric === metric}
              onClick={() => handleYAxisSelect(metric)}
            >
              {formatMetricLabel(metric)}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export default PathCharts;
